import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  CalendarDays, Sparkles, Settings2, Loader2, RotateCcw, Pencil,
  Wrench, Store, AlertTriangle, ChevronRight, EyeOff, Maximize2, X,
  Lock, KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const PLANNING_ACCESS_CODE = '9999';
const UNLOCK_KEY = 'planning_chantiers_unlocked_v1';

// Status badge colour theme keys -> tailwind classes
const STATUS_THEME = {
  slate:    'bg-slate-100  text-slate-700  border-slate-200',
  blue:     'bg-blue-50    text-blue-700   border-blue-200',
  rose:     'bg-rose-50    text-rose-700   border-rose-200',
  red:      'bg-red-100    text-red-700    border-red-300 font-semibold',
  orange:   'bg-orange-50  text-orange-700 border-orange-200',
  stone:    'bg-stone-100  text-stone-600  border-stone-200',
  emerald:  'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber:    'bg-amber-50   text-amber-700  border-amber-200',
};

const TYPE_LABEL = {
  ouverture: 'Chantier corner ouverture',
  remodeling: 'Chantier corner remodeling',
};

const TYPE_THEME = {
  ouverture: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  remodeling: 'bg-purple-50 text-purple-700 border-purple-200',
};

const fmtFr = (iso) => {
  try { return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' }); }
  catch { return iso; }
};

const monthKey = (iso) => {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  } catch { return iso; }
};

const monthLabel = (iso) => {
  try {
    const d = new Date(iso);
    const s = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch { return iso; }
};

// Helper: insert month-name separators between week cards when a new month starts
function renderTimeline(weeks, statusByKey, isAdmin, onEdit, onReset) {
  const out = [];
  let prevMonth = null;
  weeks.forEach((w) => {
    const mk = monthKey(w.monday);
    if (mk !== prevMonth) {
      out.push(
        <div key={`sep-${mk}`} className="col-span-full flex items-center gap-3 pt-2" data-testid={`month-separator-${mk}`}>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-200 to-transparent" />
          <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-amber-600 px-3 py-1 rounded-full bg-amber-50 border border-amber-200">
            {monthLabel(w.monday)}
          </span>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-amber-200 to-transparent" />
        </div>
      );
      prevMonth = mk;
    }
    out.push(
      <WeekCard
        key={w.week_id}
        week={w}
        statusByKey={statusByKey}
        isAdmin={isAdmin}
        onEdit={() => onEdit(w)}
        onReset={() => onReset(w.week_id)}
      />
    );
  });
  return out;
}

export default function PlanningChantiersPage() {
  const { token, user } = useAuth();
  const isAdmin = ['admin', 'super_admin'].includes(user?.role);
  const location = useLocation();
  const navigate = useNavigate();
  const isPresentation = location.pathname.endsWith('/presentation');

  // Access code gate (session-scoped)
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem(UNLOCK_KEY) === '1'; } catch { return false; }
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editWeek, setEditWeek] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/planning-chantiers`, { headers });
      if (!res.ok) throw new Error('load');
      setData(await res.json());
    } catch {
      toast.error('Impossible de charger le planning');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token && unlocked) load(); }, [token, unlocked]);

  const statusByKey = useMemo(() => {
    if (!data?.statuses) return {};
    return Object.fromEntries(data.statuses.map(s => [s.key, s]));
  }, [data]);

  const handleResetWeek = async (weekId) => {
    try {
      const res = await fetch(`${API}/api/planning-chantiers/weeks/${weekId}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Semaine réinitialisée'); load(); }
    } catch { toast.error('Erreur'); }
  };

  const enterPresentation = () => {
    // Tries fullscreen API + opens presentation route in new tab
    const url = '/planning-chantiers/presentation';
    const win = window.open(url, '_blank');
    if (win) {
      win.addEventListener('load', () => {
        try { win.document.documentElement.requestFullscreen?.(); } catch {}
      });
    } else {
      navigate(url);
    }
  };

  if (!unlocked) {
    const Wrapper = isPresentation ? React.Fragment : AppLayout;
    return (
      <Wrapper>
        <AccessGate
          isPresentation={isPresentation}
          onUnlock={() => {
            try { sessionStorage.setItem(UNLOCK_KEY, '1'); } catch {}
            setUnlocked(true);
          }}
        />
      </Wrapper>
    );
  }

  if (loading || !data) {
    const Wrapper = isPresentation ? React.Fragment : AppLayout;
    return (
      <Wrapper>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Wrapper>
    );
  }

  // ── Presentation Mode (no AppLayout, full-screen, larger spacing) ───────
  if (isPresentation) {
    return (
      <PresentationView data={data} statusByKey={statusByKey} />
    );
  }

  // ── Standard Mode ────────────────────────────────────────────────────────
  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="planning-chantiers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <CalendarDays className="w-7 h-7 text-amber-500" />
              Planning chantiers — Ouvertures
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Vision {data.weeks.length} semaines (~{Math.round(data.weeks.length / 4.3)} mois) · Mise en place le mardi · Ouverture le jeudi
            </p>
          </div>
          <Button onClick={enterPresentation}
            className="gap-1 bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="presentation-mode-btn">
            <Maximize2 className="w-4 h-4" /> Mode présentation
          </Button>
        </div>

        {/* Top info banner */}
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="p-4 sm:p-5">
            <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
              Nos capacités d'installation sont organisées chaque semaine selon un process
              standardisé (<span className="font-semibold text-amber-700">mise en place le mardi, ouverture le jeudi</span>)
              afin de garantir un niveau d'exécution optimal.
            </p>
          </CardContent>
        </Card>

        {/* Weeks timeline (with month separators) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="planning-grid">
          {renderTimeline(data.weeks, statusByKey, isAdmin,
            (w) => setEditWeek(w),
            (id) => handleResetWeek(id)
          )}
        </div>

        {/* Bottom disclaimer */}
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-3 text-center">
            <p className="text-xs text-slate-500 italic">
              Planning indicatif des capacités chantiers, sous réserve de validation
              technique, contractuelle et logistique.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Admin: edit week dialog */}
      {isAdmin && editWeek && (
        <EditWeekDialog
          week={editWeek}
          regions={data.regions}
          statuses={data.statuses}
          token={token}
          onClose={() => setEditWeek(null)}
          onSaved={() => { setEditWeek(null); load(); }}
        />
      )}
    </AppLayout>
  );
}

// ============================================================================
// Access Gate — code-protected entry (4-digit PIN)
function AccessGate({ isPresentation, onUnlock }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const submit = (e) => {
    e?.preventDefault?.();
    if (code === PLANNING_ACCESS_CODE) {
      setError(false);
      onUnlock();
      toast.success('Accès autorisé');
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (code.length === 4) {
      // small delay so user sees the 4th digit
      const id = setTimeout(submit, 120);
      return () => clearTimeout(id);
    }
    setError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const containerCls = isPresentation
    ? 'min-h-screen w-full bg-bg flex items-center justify-center px-4'
    : 'flex items-center justify-center py-16 px-4';

  return (
    <div className={containerCls} data-testid="planning-access-gate">
      <div className="w-full max-w-[400px] rounded-card bg-surface p-7 sm:p-8 space-y-5 text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-surface-2 flex items-center justify-center">
          <Lock className="w-8 h-8 text-text-2" />
        </div>
        <div>
          <h2 className="font-display font-bold uppercase tracking-[0.02em] text-ink text-2xl flex items-center justify-center gap-2">
            <CalendarDays className="w-6 h-6 text-text-2" /> Planning chantiers
          </h2>
          <p className="text-sm text-text-3 mt-1.5">
            Module protégé — saisissez le code d'accès pour continuer.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            autoFocus
            maxLength={4}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="• • • •"
            className={`w-full h-14 text-center text-3xl tracking-[0.6em] font-mono font-bold border rounded-ctrl outline-none transition-all
              ${error ? 'border-danger text-danger' : 'border-border bg-surface text-ink focus:ring-2 focus:ring-orange'}
              ${shake ? 'animate-pulse' : ''}`}
            data-testid="planning-access-code-input"
          />
          {error && (
            <p className="text-xs text-danger font-medium" data-testid="planning-access-error">
              Code incorrect — réessayez
            </p>
          )}
          <button type="submit" disabled={code.length !== 4}
            className="w-full h-11 inline-flex items-center justify-center gap-1.5 rounded-ctrl text-sm font-bold bg-orange text-orange-on transition-colors hover:brightness-105 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="planning-access-submit">
            <KeyRound className="w-4 h-4" /> Déverrouiller
          </button>
        </form>
        <p className="text-[12px] text-text-3">
          Le code expire à la fermeture de l'onglet.
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Presentation Mode — full-screen, no sidebar/header, large text, subtle texture
function PresentationView({ data, statusByKey }) {
  const [tick, setTick] = React.useState(0);
  // Auto-refresh every 5min so a left-open presentation stays current
  React.useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 300000);
    return () => clearInterval(id);
  }, []);
  // tick is unused but keeps the comp re-renderable (data is fed by parent)
  void tick;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-amber-50/20 to-slate-100 print:bg-white"
      data-testid="presentation-view"
      style={{ backgroundImage: "radial-gradient(circle at 20% 10%, rgba(251,191,36,0.06), transparent 40%), radial-gradient(circle at 80% 90%, rgba(244,114,182,0.05), transparent 40%)" }}>
      <div className="max-w-[1800px] mx-auto px-8 py-8">
        {/* Top bar with title + close */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold">Wasabi Business · Présentation</p>
            <h1 className="text-4xl lg:text-5xl font-extrabold text-slate-900 flex items-center gap-3 mt-1">
              <CalendarDays className="w-10 h-10 text-amber-500" />
              Planning chantiers — Ouvertures
            </h1>
            <p className="text-base text-slate-600 mt-1">
              Vision {data.weeks.length} semaines (~{Math.round(data.weeks.length / 4.3)} mois) · <span className="font-semibold text-amber-700">Mise en place le mardi</span> · <span className="font-semibold text-emerald-700">Ouverture le jeudi</span>
            </p>
          </div>
          <Button variant="outline" onClick={() => window.close()} className="gap-1" data-testid="presentation-close-btn">
            <X className="w-4 h-4" /> Quitter
          </Button>
        </div>

        {/* Top banner */}
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 mb-6">
          <CardContent className="p-5">
            <p className="text-base text-slate-700 leading-relaxed">
              Nos capacités d'installation sont organisées chaque semaine selon un process
              standardisé (<span className="font-semibold text-amber-700">mise en place le mardi, ouverture le jeudi</span>)
              afin de garantir un niveau d'exécution optimal.
            </p>
          </CardContent>
        </Card>

        {/* Grid - larger 4 cols on big screens, with month separators */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {renderTimeline(data.weeks, statusByKey, false, () => {}, () => {})}
        </div>

        {/* Bottom disclaimer */}
        <Card className="border-slate-200 bg-slate-50/80 mt-6">
          <CardContent className="p-4 text-center">
            <p className="text-sm text-slate-500 italic">
              Planning indicatif des capacités chantiers, sous réserve de validation
              technique, contractuelle et logistique.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ============================================================================
function WeekCard({ week, statusByKey, isAdmin, onEdit, onReset }) {
  const isPast = week.offset === 0;
  const isArbitrage = week.arbitrage_note;
  const dimmed = !week.active;

  return (
    <Card
      className={`relative border-l-4 transition-all ${dimmed ? 'opacity-60 border-l-slate-300' : 'border-l-amber-400 hover:shadow-md'}`}
      data-testid={`week-card-${week.week_id}`}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header: week number + period */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Semaine</p>
              <p className="text-2xl font-bold text-slate-900 leading-none" data-testid={`week-number-${week.week_id}`}>
                S{week.iso_week}
              </p>
              {isPast && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]" data-testid={`week-current-${week.week_id}`}>
                  En cours
                </Badge>
              )}
              {dimmed && (
                <Badge variant="outline" className="text-[10px] gap-1"><EyeOff className="w-3 h-3" />Désactivée</Badge>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{week.period_label}</p>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-amber-600"
                onClick={onEdit} title="Modifier"
                data-testid={`edit-week-${week.week_id}`}><Pencil className="w-3.5 h-3.5" /></Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-red-600"
                onClick={onReset} title="Réinitialiser"
                data-testid={`reset-week-${week.week_id}`}><RotateCcw className="w-3.5 h-3.5" /></Button>
            </div>
          )}
        </div>

        {/* Fixed days */}
        <div className="flex items-center gap-2 text-xs">
          <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-md border border-blue-200">
            <Wrench className="w-3 h-3" /> Mardi · Mise en place
          </span>
          <span className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-md border border-emerald-200">
            <Store className="w-3 h-3" /> Jeudi · Ouverture
          </span>
        </div>

        {/* Arbitrage note (S+8/S+9) */}
        {isArbitrage && (
          <div className="flex items-center gap-1.5 text-[11px] text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-2 py-1.5">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span>Créneau en cours d'arbitrage</span>
          </div>
        )}

        {/* Chantiers */}
        <div className="space-y-2 pt-1">
          {week.chantiers.length === 0 ? (
            <p className="text-xs text-slate-400 italic text-center py-2">Aucun chantier planifié</p>
          ) : (
            week.chantiers.map((c, idx) => {
              const st = statusByKey[c.status];
              const stCls = STATUS_THEME[st?.color] || STATUS_THEME.slate;
              const typeCls = TYPE_THEME[c.type] || TYPE_THEME.ouverture;
              return (
                <div key={idx} className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-1.5" data-testid={`chantier-${week.week_id}-${idx}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm text-slate-900 truncate">{c.region}</p>
                    <Badge variant="outline" className={`${stCls} text-[10px] shrink-0 px-1.5`}>
                      {st?.label || c.status}
                    </Badge>
                  </div>
                  <Badge variant="outline" className={`${typeCls} text-[10px] gap-1`}>
                    {c.type === 'remodeling' ? <Wrench className="w-2.5 h-2.5" /> : <Store className="w-2.5 h-2.5" />}
                    {TYPE_LABEL[c.type] || c.type}
                  </Badge>
                  <p className="text-[11px] text-slate-400 flex items-center gap-1 select-none">
                    <ChevronRight className="w-3 h-3" />Détails +
                  </p>
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
export function SettingsDialog({ open, onClose, token, onSaved }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regionsText, setRegionsText] = useState('');
  const [statusesJson, setStatusesJson] = useState('');

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/planning-chantiers/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()).then((s) => {
      setSettings(s);
      setRegionsText((s.regions || []).join('\n'));
      setStatusesJson(JSON.stringify(s.statuses || [], null, 2));
    });
  }, [open, token]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    try {
      const regions = regionsText.split('\n').map(s => s.trim()).filter(Boolean);
      let statuses = settings.statuses;
      try { statuses = JSON.parse(statusesJson); } catch { toast.error('JSON statuts invalide'); setSaving(false); return; }
      const body = {
        regions,
        statuses,
        week_count: parseInt(settings.week_count, 10),
        two_chantiers_ratio: parseFloat(settings.two_chantiers_ratio),
        remodeling_ratio: parseFloat(settings.remodeling_ratio),
      };
      const res = await fetch(`${API}/api/planning-chantiers/settings`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur');
      } else {
        toast.success('Paramètres enregistrés');
        onSaved();
        onClose();
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="planning-settings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-amber-500" /> Paramètres du planning
          </DialogTitle>
        </DialogHeader>
        {!settings ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-8 text-amber-500" /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Nombre de semaines</Label>
                <Input type="number" min="4" max="26" value={settings.week_count}
                  onChange={(e) => setSettings({ ...settings, week_count: e.target.value })}
                  data-testid="setting-week-count" />
              </div>
              <div>
                <Label className="text-xs">% semaines à 2 chantiers</Label>
                <Input type="number" step="0.05" min="0" max="1" value={settings.two_chantiers_ratio}
                  onChange={(e) => setSettings({ ...settings, two_chantiers_ratio: e.target.value })}
                  data-testid="setting-two-ratio" />
                <p className="text-[10px] text-slate-400 mt-0.5">0.30 → 0.40 conseillé</p>
              </div>
              <div>
                <Label className="text-xs">% remodeling</Label>
                <Input type="number" step="0.05" min="0" max="1" value={settings.remodeling_ratio}
                  onChange={(e) => setSettings({ ...settings, remodeling_ratio: e.target.value })}
                  data-testid="setting-remodeling-ratio" />
                <p className="text-[10px] text-slate-400 mt-0.5">0.30 conseillé (70% ouverture)</p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Régions (1 par ligne)</Label>
              <textarea value={regionsText} onChange={(e) => setRegionsText(e.target.value)}
                className="w-full border rounded-md p-2 text-sm font-mono min-h-[140px]"
                data-testid="setting-regions" />
            </div>
            <div>
              <Label className="text-xs">Statuts (JSON : key, label, color)</Label>
              <textarea value={statusesJson} onChange={(e) => setStatusesJson(e.target.value)}
                className="w-full border rounded-md p-2 text-xs font-mono min-h-[140px]"
                data-testid="setting-statuses" />
              <p className="text-[10px] text-slate-400 mt-0.5">
                Couleurs: slate, blue, rose, red, orange, stone, emerald, amber
              </p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="save-planning-settings">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
function EditWeekDialog({ week, regions, statuses, token, onClose, onSaved }) {
  const [chantiers, setChantiers] = useState(
    (week.chantiers || []).map(c => ({ ...c }))
  );
  const [active, setActive] = useState(week.active !== false);
  const [saving, setSaving] = useState(false);

  const addChantier = () => setChantiers([
    ...chantiers,
    { region: regions[0] || '', type: 'ouverture', status: statuses[0]?.key || 'planifie' },
  ]);
  const removeChantier = (idx) => setChantiers(chantiers.filter((_, i) => i !== idx));
  const update = (idx, key, value) => setChantiers(chantiers.map((c, i) => i === idx ? { ...c, [key]: value } : c));

  const save = async () => {
    if (chantiers.length > 2) { toast.error('Max 2 chantiers par semaine'); return; }
    if (chantiers.length === 2 && chantiers[0].region === chantiers[1].region) {
      toast.error('Les 2 chantiers doivent avoir des régions différentes'); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/planning-chantiers/weeks/${week.week_id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ chantiers, active }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); toast.error(err.detail || 'Erreur'); }
      else { toast.success('Semaine mise à jour'); onSaved(); }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="edit-week-modal">
        <DialogHeader>
          <DialogTitle>Modifier la semaine S{week.iso_week}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="week-active" />
            Semaine active
          </label>
          {chantiers.map((c, idx) => (
            <div key={idx} className="border rounded-md p-3 space-y-2 bg-slate-50">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-500">Chantier {idx + 1}</p>
                <Button size="sm" variant="ghost" onClick={() => removeChantier(idx)} className="h-7 px-2 text-red-500">Retirer</Button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <select value={c.region} onChange={(e) => update(idx, 'region', e.target.value)}
                  className="text-xs border rounded px-2 py-1.5 bg-white" data-testid={`week-region-${idx}`}>
                  {regions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <select value={c.type} onChange={(e) => update(idx, 'type', e.target.value)}
                  className="text-xs border rounded px-2 py-1.5 bg-white" data-testid={`week-type-${idx}`}>
                  <option value="ouverture">Ouverture</option>
                  <option value="remodeling">Remodeling</option>
                </select>
                <select value={c.status} onChange={(e) => update(idx, 'status', e.target.value)}
                  className="text-xs border rounded px-2 py-1.5 bg-white" data-testid={`week-status-${idx}`}>
                  {statuses.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            </div>
          ))}
          {chantiers.length < 2 && (
            <Button variant="outline" onClick={addChantier} className="w-full gap-1 text-xs" data-testid="add-chantier-btn">
              + Ajouter un chantier
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="save-week-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
